import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { applyColorScheme, getStoredColorScheme, type ColorScheme } from './color-scheme.js';

interface ThemeContextValue {
  theme: ColorScheme;
  setTheme: (theme: ColorScheme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Provides light/dark color-scheme state for apps that consume `@nrx/ui`.
 * Applies `data-theme` on the document root so `theme.css` token overrides take effect.
 */
export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  const [theme, setThemeState] = useState<ColorScheme>(() => getStoredColorScheme());

  useEffect(() => {
    applyColorScheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: ColorScheme) => {
    applyColorScheme(next);
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: ColorScheme = current === 'light' ? 'dark' : 'light';
      applyColorScheme(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Access the current color scheme and toggle helpers. */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
